import React, { useState } from 'react';
import { useParamountAuth } from 'context/ParamountAuthContext';
import { superUserShowByEmail, superUserDeleteByEmail } from '../../../services/clients.svc';
import './DeleteUserPage.scss';

const isObject = (val) => val && typeof val === 'object' && !Array.isArray(val);

function ObjectViewer({ data, title }) {
  if (data === null || data === undefined) {
    return <span className="viewer-null">null</span>;
  }

  if (typeof data !== 'object') {
    return <span className="viewer-primitive">{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <span>Empty Array</span>;
    
    // Check if it's an array of objects
    const isArrayOfObjects = data.every(isObject);
    if (!isArrayOfObjects) {
      return <span>{data.join(', ')}</span>;
    }

    // Array of objects -> render as a table
    const columns = Array.from(new Set(data.flatMap(Object.keys)));
    
    return (
      <div className="viewer-table-container">
        {title && <h4>{title}</h4>}
        <div className="viewer-table-wrapper">
          <table className="viewer-table">
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {columns.map(col => (
                    <td key={col}>
                      <ObjectViewer data={row[col]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Object -> render as key-value rows
  const ObjectUI = (
    <table className="viewer-table object-table">
      <tbody>
        {Object.keys(data).map(k => (
          <tr key={k}>
            <td className="viewer-key"><strong>{k}</strong></td>
            <td className="viewer-value">
              <ObjectViewer data={data[k]} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="viewer-object-container">
      {title && <h4>{title}</h4>}
      {ObjectUI}
    </div>
  );
}

export default function DeleteUserPage() {
  const { userRoles } = useParamountAuth();
  const [email, setEmail] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Removed confirmDelete, not used
  const isSuperUser = (userRoles || []).includes('SuperUser');

  if (!isSuperUser) {
    return <div>Unauthorized</div>;
  }

  const handleShowUser = async () => {
    setLoading(true);
    setError("");
    setUserInfo(null);
    if (!email) {
      setError("Please enter an email address.");
      setLoading(false);
      return;
    }
    try {
      const result = await superUserShowByEmail(email);
      if (result && result.user) {
        setUserInfo(result);
      } else {
        setError(result?.error || "User not found.");
      }
    } catch (err) {
      setError(err?.message || "Error fetching user.");
    } finally {
      setLoading(false);
    }
  };
  // handleDeleteUser removed; logic now in button onClick

  return (
    <div className="delete-user-page-container">
      <div className="delete-user-page-title">Delete User - This action is irreversible.</div>
      <input
        className="delete-user-page-input"
        type="email"
        placeholder="Enter user email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        disabled={loading}
      />
      <div className="delete-user-page-buttons">
        <button onClick={handleShowUser} disabled={!email || loading}>Show User</button>
        <button
          onClick={async () => {
            setError("");
            if (!email) {
              setError("Please enter an email address.");
              return;
            }
            if (!window.confirm(`Are you sure you want to delete the user: ${email}? This action is irreversible.`)) {
              return;
            }
            setLoading(true);
            try {
              const result = await superUserDeleteByEmail(email);
              if (result && result.success) {
                setUserInfo(null);
                setEmail("");
              } else {
                setError(result?.error || "Delete failed.");
              }
            } catch (err) {
              setError("Error deleting user.");
            } finally {
              setLoading(false);
            }
          }}
          disabled={!userInfo || loading}
          style={{ background: '#e00', color: '#fff' }}
        >
          Delete
        </button>
      </div>
      {error && <div className="delete-user-page-error">{error}</div>}
      {userInfo && (
        <div className="delete-user-page-data" style={{ marginTop: '20px' }}>
          <ObjectViewer data={userInfo} title="User Details" />
        </div>
      )}
    </div>
  );
}
